import React, { useEffect } from 'react';
import { COMPANY } from '../lib/company';

const LAST_UPDATED = 'August 12, 2026';

const TermsPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Terms of Service | Thornless Data Management';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Terms of service for Thornless Data Management, a Kuiken Technologies LLC service.');
  }, []);

  const section = (title: string, children: React.ReactNode) => (
    <div style={{ marginBottom: '2.5rem' }}>
      <h2 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text)', marginBottom: '0.75rem' }}>{title}</h2>
      <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-muted)', lineHeight: 'var(--line-height-relaxed)' }}>{children}</div>
    </div>
  );

  const shout = (children: React.ReactNode) => (
    <p style={{ textTransform: 'uppercase', fontWeight: 'var(--font-weight-semibold)' }}>{children}</p>
  );

  return (
    <main>
      <section style={{ backgroundColor: 'var(--color-primary)', color: '#ffffff', padding: '4rem 0 3rem' }}>
        <div className="layout-container">
          <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '0.75rem' }}>Legal</p>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'var(--font-weight-extrabold)', lineHeight: 'var(--line-height-tight)', color: '#ffffff', marginBottom: '1rem' }}>Terms of Service</h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 'var(--font-size-base)' }}>Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="layout-container section-padding">
        <div style={{ maxWidth: '760px' }}>

          {section('1. Agreement to Terms', (
            <p>
              By accessing or using the {COMPANY.tradeName} website ({COMPANY.domain}) or engaging {COMPANY.tradeName} for
              services, you agree to be bound by these Terms of Service, which form a binding agreement between you and{' '}
              {COMPANY.legalName}. {COMPANY.tradeName} is a service of {COMPANY.legalName}. If you do not agree to these
              terms, do not use our website or services.
            </p>
          ))}

          {section('2. Description of Services', (
            <p>
              {COMPANY.tradeName} provides data engineering, analytics, business intelligence, agentic AI automation, and
              data warehousing consulting services to businesses. The specific scope, deliverables, timeline, and fees for
              any engagement are defined in a separate written agreement (a Statement of Work or Service Agreement) executed
              between the client and {COMPANY.legalName} prior to the commencement of work. We may add, modify, or
              discontinue features of this website or our general service offerings at any time; changes to an active,
              signed engagement require mutual written agreement.
            </p>
          ))}

          {section('3. Eligibility', (
            <p>
              You must be at least 18 years old to use this website or engage our services. If you are entering an
              engagement on behalf of a company or other legal entity, you represent that you have the authority to bind
              that entity to these Terms and to any Statement of Work.
            </p>
          ))}

          {section('4. Accounts and Security', (
            <p>
              This website does not offer user accounts or logins. Where a client engagement grants us access to your
              systems (for example, a cloud data platform or reporting tool), you are responsible for provisioning that
              access appropriately and for promptly revoking it when no longer needed; we are responsible for using only the
              access granted to us and only for the purposes of delivering the agreed services. We reserve the right to
              suspend our own systems' access to client environments if we reasonably believe that access is being misused
              or poses a security risk.
            </p>
          ))}

          {section('5. Fees, Payment, and Billing', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>
                This website does not process payments or sell services directly online. Pricing, invoicing schedule,
                payment terms, late-payment consequences, and any auto-renewal terms for a given engagement are set out
                exclusively in that engagement's Statement of Work or Service Agreement, which controls over any general
                statement on this website.
              </p>
              <p style={{ marginBottom: '0.75rem' }}>
                We may change our general/listed pricing for future engagements at any time; changes do not apply
                retroactively to a signed, active engagement.
              </p>
              <p>
                <strong>Refunds and cancellation timing.</strong> Refund eligibility, and any cancellation notice period or
                kill fee for work already performed, are defined in the applicable Statement of Work. Absent a specific
                refund term in that agreement, fees for work already performed are non-refundable, and fees for work not yet
                performed are refunded on a pro-rata basis following written cancellation. If a payment is charged back or
                disputed with a payment processor or bank without first contacting us, we may suspend in-progress work and
                deliverable access until the dispute is resolved.
              </p>
            </>
          ))}

          {section('6. Your Content and Responsibilities', (
            <p>
              Where an engagement involves content, data, or materials you provide to us ("Your Content"), you retain
              ownership of Your Content. You grant us a limited license to use, process, and store Your Content solely to
              deliver the agreed services. You represent that you have the necessary rights to provide Your Content to us
              and that our intended use of it will not infringe any third party's rights. If we build or operate a website,
              dashboard, or system on your behalf, you are responsible for that property's own legal compliance going
              forward (for example, its own privacy policy, cookie consent, and applicable industry regulations) unless a
              signed Statement of Work expressly assigns us an ongoing compliance role.
            </p>
          ))}

          {section('7. Confidentiality', (
            <p>
              Both parties to a service engagement agree to treat each other's confidential business information with
              appropriate care. We will not disclose client data, systems access, or proprietary business information to
              third parties except as required to deliver the agreed services, as required by law, or as permitted under
              §9, "Intellectual Property," regarding our own reusable tools and methodologies. Specific confidentiality
              obligations for an engagement are governed by that engagement's Statement of Work.
            </p>
          ))}

          {section('8. Website Use', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>You agree to use this website only for lawful purposes and in a manner that does not infringe the rights of others. You may not:</p>
              <ul style={{ paddingLeft: '1.5rem' }}>
                <li style={{ marginBottom: '0.4rem' }}>Attempt to gain unauthorized access to any part of our website or systems</li>
                <li style={{ marginBottom: '0.4rem' }}>Use automated tools to scrape, index, or harvest content from this website</li>
                <li style={{ marginBottom: '0.4rem' }}>Transmit any malicious code, spam, or disruptive content</li>
                <li style={{ marginBottom: '0.4rem' }}>Misrepresent your identity or affiliation when submitting inquiries</li>
              </ul>
            </>
          ))}

          {section('9. Intellectual Property', (
            <p>
              All content on this website, including text, graphics, logos, and code, is the property of{' '}
              {COMPANY.legalName} and is protected by applicable intellectual property laws. You may not reproduce,
              distribute, or create derivative works from our website content without prior written permission. For a
              signed client engagement: upon full payment of all fees due for that engagement, the client owns the specific
              deliverables created for them under that engagement, as identified in the Statement of Work. We retain
              ownership of our own pre-existing tools, frameworks, internal libraries, methodologies, and templates used to
              build those deliverables, and may reuse them in other engagements, provided we do not disclose your
              confidential data in doing so.
            </p>
          ))}

          {section('10. AI and Generated Output', (
            <p>
              Some deliverables (for example, agentic AI automation) may include output generated in whole or part by
              third-party AI systems. AI-generated output can be inaccurate, incomplete, or require human review before use
              — we review what we deliver, but you are also responsible for reviewing deliverables before relying on them
              for a consequential decision. We do not warrant that AI-generated output is unique or free of similarity to
              other content, and the copyright status of AI-generated material is unsettled under current law. We do not
              warrant that AI-generated output is free of third-party intellectual property claims, though we take
              reasonable steps (such as using reputable providers under standard commercial terms) to reduce that risk.
            </p>
          ))}

          {section('11. Third-Party Services', (
            <p>
              Our website and services rely on third-party providers, including Cloudflare, Supabase, Resend, and Google
              Analytics, and, for AI-based deliverables, providers such as OpenAI, Anthropic, Microsoft Azure, and AWS. We
              are not responsible or liable for outages, errors, security incidents, or acts or omissions of these
              third-party providers, though we will work in good faith to mitigate the impact on you where reasonably
              possible.
            </p>
          ))}

          {section('12. Service Availability', (
            <p>
              We do not guarantee that this website will be uninterrupted, error-free, or available at all times. Project
              timelines communicated in a Statement of Work, on this website, or in correspondence are targets based on our
              best estimate at the time, not guarantees — actual delivery dates can be affected by factors including client
              responsiveness, data quality, and third-party dependencies outside our control.
            </p>
          ))}

          {section('13. Cancellation and Termination', (
            <p>
              Either party may terminate an active engagement as permitted by that engagement's Statement of Work,
              typically on written notice. On termination, we will make reasonable efforts to provide you with an export of
              your data and any completed deliverables you have paid for. Sections of these Terms that by their nature
              should survive termination — including Intellectual Property, AI and Generated Output, Disclaimer of
              Warranties, Limitation of Liability, Indemnification, and Governing Law and Disputes — remain in effect after
              termination.
            </p>
          ))}

          {section('14. Disclaimer of Warranties', (
            <>
              {shout('THIS WEBSITE AND ANY INFORMATION ON IT ARE PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.')}
              {shout('WE DO NOT WARRANT THAT THE WEBSITE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. WE DO NOT GUARANTEE ANY SPECIFIC LEVEL OF REVENUE, TRAFFIC, SEARCH RANKING, OR OTHER BUSINESS OUTCOME FROM OUR SERVICES.')}
              <p style={{ marginTop: '0.75rem' }}>
                Results from consulting engagements depend on the quality of client data, cooperation, and business
                conditions, and no specific outcome is guaranteed unless expressly stated in a signed agreement.
              </p>
            </>
          ))}

          {section('15. Limitation of Liability', (
            <>
              {shout(`TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ${COMPANY.legalName.toUpperCase()} AND ${COMPANY.tradeName.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITY, ARISING FROM YOUR USE OF THIS WEBSITE OR OUR SERVICES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.`)}
              {shout('OUR TOTAL LIABILITY FOR ANY CLAIM ARISING UNDER THESE TERMS OR A SERVICE ENGAGEMENT SHALL NOT EXCEED THE GREATER OF (A) THE TOTAL FEES PAID BY THE CLIENT FOR THE SPECIFIC SERVICES GIVING RISE TO THE CLAIM IN THE 12 MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100).')}
              <p style={{ marginTop: '0.75rem' }}>
                Nothing in these Terms excludes or limits liability that cannot be excluded or limited under applicable law,
                including liability for gross negligence, willful misconduct, or fraud.
              </p>
            </>
          ))}

          {section('16. Indemnification', (
            <p>
              You agree to indemnify and hold harmless {COMPANY.legalName}, its owners, employees, and contractors from any
              claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from Your Content, your
              misuse of our website or services, or your violation of these Terms or of any third party's rights.
            </p>
          ))}

          {section('17. Governing Law and Disputes', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>
                These Terms are governed by and construed in accordance with the laws of the State of {COMPANY.state},
                without regard to its conflict of law provisions.
              </p>
              <p style={{ marginBottom: '0.75rem' }}>
                Before filing a claim, we ask that you contact us at the address in §18 and give us 30 days to resolve the
                dispute informally. If we cannot resolve it informally, any dispute arising from these Terms or your use of
                our website or services is subject to the exclusive jurisdiction of the state and federal courts located in{' '}
                {COMPANY.state}, and each party consents to personal jurisdiction there.
              </p>
              <p>
                This exclusive-venue provision does not limit either party's right to seek injunctive or other equitable
                relief in any court of competent jurisdiction to protect its intellectual property or confidential
                information, or to bring an individual claim in small claims court where eligible.
              </p>
            </>
          ))}

          {section('18. Changes to These Terms', (
            <p>
              We may update these Terms of Service at any time. Changes will be posted on this page with an updated "Last
              updated" date. For material changes affecting an active paid engagement, we will provide reasonable advance
              notice by email or in writing before the change takes effect for that engagement. Continued use of our
              website following any changes constitutes acceptance of the revised terms.
            </p>
          ))}

          {section('19. Miscellaneous', (
            <p>
              If any provision of these Terms is found unenforceable, the remaining provisions remain in full effect. Our
              failure to enforce any provision is not a waiver of that provision. You may not assign these Terms or any
              engagement without our prior written consent; we may assign these Terms in connection with a merger,
              acquisition, or sale of assets. These Terms, together with any signed Statement of Work, constitute the
              entire agreement between you and {COMPANY.legalName} regarding their subject matter.
            </p>
          ))}

          {section('20. Contact Us', (
            <>
              <p style={{ marginBottom: '0.5rem' }}>If you have questions about these Terms of Service, contact us:</p>
              <p style={{ margin: 0 }}>{COMPANY.legalName} ({COMPANY.tradeName})</p>
              {COMPANY.addressLines.map((line) => <p key={line} style={{ margin: 0 }}>{line}</p>)}
              <p style={{ marginTop: '0.5rem' }}>
                Email: <a href={`mailto:${COMPANY.email}`} style={{ color: 'var(--color-primary)' }}>{COMPANY.email}</a>
              </p>
              <p>Or via <a href="/contact" style={{ color: 'var(--color-primary)' }}>our contact page</a>.</p>
            </>
          ))}

        </div>
      </section>
    </main>
  );
};

export default TermsPage;
